import { useSelector } from '@xstate/react';
import { MemoId } from 'models/Memo';
import { useEffect } from 'react';
import {
  ClosingBehaviour,
  EditorMode,
  StateValue,
} from 'xstate/machines/EditorMachine';
import { EditorActor } from 'xstate/actors/EditorActor';

interface UseEditorResult {
  editorState: ReturnType<typeof EditorActor.getSnapshot>;
  isClosed: boolean;
  isOpen: boolean;
  memoId?: MemoId;
  mode?: EditorMode;
  closingBehavior?: ClosingBehaviour;
  canToggleMode?: boolean;
}

export const useEditor = (): UseEditorResult => {
  const editorState = useSelector(EditorActor, (snapshot) => snapshot);

  // State Helpers
  const isClosed = editorState.matches(StateValue.Closed);
  const isOpen = editorState.matches(StateValue.Open);
  const mode = editorState.context.mode;
  const closingBehavior = editorState.context.closingBehavior;
  const canToggleMode = editorState.context.canToggleMode;

  useEffect(() => {
    if (isOpen) {
      // Disable page scroll
      document.body.style.overflow = 'hidden';
    } else {
      // Enable page scroll
      document.body.style.overflow = '';
    }

    // Cleanup function to restore scroll when the component unmounts
    return () => {
      document.body.style.overflow = '';
    };
  }, [editorState]);

  // Context Helpers
  const memoId = editorState.context.memoId;

  return {
    editorState,
    isClosed,
    isOpen,
    memoId,
    mode,
    closingBehavior,
    canToggleMode,
  };
};
